// What signed-out iPads sent (gap #18, office-review prototype, F-57): commands that arrived after
// the office signed an iPad out, or that the server refused as done on it, wait here unapplied
// (sync.quarantine.list). An owner or manager applies one, once, as the technician who sent it, or
// discards it (sync.quarantine.resolve); a refusal stays open with the server's words. Review queue
// only, as the prototype has it.

import { Fragment, useEffect, useState } from 'react';
import { dateText, dateTimeText } from '@flowology/kernel';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';

type Status = 'open' | 'applied' | 'discarded';
export interface QuarantineItem {
  id: string;
  staffName: string | null;
  deviceLabel: string | null;
  deviceRevokedAt: string | null;
  deviceRevokedReason: string | null;
  deviceWipedAt: string | null;
  deviceLastSeenAt: string | null;
  action: string;
  title: string;
  jobNumber: string | null;
  occurredAt: string | null;
  receivedAt: string;
  status: Status;
}

/** "Sam's iPad · signed out Oct 15 · wiped Oct 16" (or "not wiped yet, last seen …"). */
export function deviceText(q: QuarantineItem, timeZone: string): string {
  const day = (at: string) => dateText(at, timeZone, { year: false });
  return [
    q.deviceLabel ?? 'An iPad',
    q.deviceRevokedAt ? `signed out ${day(q.deviceRevokedAt)}` : null,
    q.deviceWipedAt
      ? `wiped ${day(q.deviceWipedAt)}`
      : q.deviceRevokedAt
        ? `not wiped yet${q.deviceLastSeenAt ? `, last seen ${day(q.deviceLastSeenAt)}` : ''}`
        : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const SHOW: [Status, string][] = [
  ['open', 'Waiting'],
  ['applied', 'Applied'],
  ['discarded', 'Discarded'],
];

export function QuarantineSection({ tick, timeZone }: { tick: number; timeZone: string }) {
  const [show, setShow] = useState<Status>('open');
  const [items, setItems] = useState<QuarantineItem[] | null>(null);
  const [again, setAgain] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [discarding, setDiscarding] = useState<string | null>(null);
  useEffect(() => {
    void client
      .call<{ items: QuarantineItem[] }>('sync.quarantine.list', { status: show })
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
  }, [show, tick, again]);
  // Nothing has ever waited and nothing waits now: the section stays out of the way.
  if (items === null || (show === 'open' && items.length === 0)) return null;
  const resolve = async (q: QuarantineItem, decision: 'apply' | 'discard') => {
    setBusy(q.id);
    try {
      await client.call('sync.quarantine.resolve', { id: q.id, decision });
      setWhy(({ [q.id]: _, ...rest }) => rest);
      setDiscarding(null);
      setAgain((n) => n + 1);
    } catch (e) {
      setWhy((w) => ({ ...w, [q.id]: e instanceof RpcError ? e.message : 'That did not work. Try again.' }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="sect quarantine" aria-label="Sent from signed-out iPads">
      <h2>Sent from signed-out iPads{show === 'open' ? ` (${items.length})` : ''}</h2>
      <p className="muted small">
        These came from an iPad after it was signed out, or the server couldn’t apply them as done, so nothing
        happened yet. Apply one (it runs once, as the technician who sent it) or discard it.
      </p>
      <div className="seg" role="group" aria-label="Show">
        {SHOW.map(([s, label]) => (
          <button
            key={s}
            className={`btn quiet${show === s ? ' on' : ''}`}
            aria-pressed={show === s}
            onClick={() => setShow(s)}
          >
            {label}
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <p className="muted">Nothing here.</p>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label="Sent from signed-out iPads">
            <thead>
              <tr>
                <th>Arrived</th>
                <th>From</th>
                <th>What it did</th>
                <th>Job</th>
                {show === 'open' && <th aria-label="Decide" />}
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <Fragment key={q.id}>
                  <tr>
                    <td className="nowrap">{dateTimeText(q.receivedAt, timeZone, { year: false })}</td>
                    <td>
                      <b>{q.staffName ?? 'A technician'}</b>
                      <div className="muted small">{deviceText(q, timeZone)}</div>
                    </td>
                    <td>
                      <span title={q.action}>{q.title}</span>
                      {q.occurredAt && (
                        <div className="muted small">
                          done on the iPad {dateTimeText(q.occurredAt, timeZone, { year: false })}
                        </div>
                      )}
                    </td>
                    <td className="nowrap">{q.jobNumber ? `#${q.jobNumber}` : '—'}</td>
                    {show === 'open' && (
                      <td>
                        {discarding === q.id ? (
                          <div className="row">
                            <SaveButton
                              className="btn danger"
                              busy={busy === q.id}
                              busyLabel="Discarding…"
                              onClick={() => void resolve(q, 'discard')}
                            >
                              Discard it
                            </SaveButton>
                            <button className="btn quiet" onClick={() => setDiscarding(null)}>
                              Keep it waiting
                            </button>
                          </div>
                        ) : (
                          <div className="row">
                            <SaveButton
                              busy={busy === q.id}
                              busyLabel="Applying…"
                              disabled={busy !== null}
                              onClick={() => void resolve(q, 'apply')}
                            >
                              Apply
                            </SaveButton>
                            <button
                              className="btn soft"
                              disabled={busy !== null}
                              onClick={() => setDiscarding(q.id)}
                            >
                              Discard…
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                  {why[q.id] && (
                    <tr>
                      <td colSpan={5} className="why-row" role="alert">
                        {why[q.id]}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
